/**
 * Dutch locale date formatters for the Polling section.
 *
 * Centralizes all date/time formatting used across Polling components.
 * Uses nl-NL locale for consistency with the Dutch-language UI.
 */
import { formatDistanceToNowStrict } from 'date-fns'
import { nl } from 'date-fns/locale'

const MONTH_NAMES_NL = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']
const DAY_NAMES_NL = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za']

/**
 * Format a date string to Dutch short format.
 * @param {string|Date} d - Date string or Date object
 * @returns {string} e.g. "3 mrt 2026"
 */
export function formatDateNL(d) {
  if (!d) return '\u2014'
  return new Date(d).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Format a date string to Dutch date+time format.
 * @param {string|Date} d - Date string or Date object
 * @returns {string} e.g. "3 mrt 2026 14:30"
 */
export function formatDateTimeNL(d) {
  if (!d) return '\u2014'
  return new Date(d).toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Street and house number of a meter's address. A CVZ address is shown without its
 * 'CVZ3' suffix: each meter of the CVZ carries its own fake number.
 * @param {object} meterDetail - from /meters/{serial}
 * @param {string} kind - regular | cvz | placeholder (useAddressDevices)
 * @returns {string} e.g. "Koninginneweg 25A1-25K2"
 */
export function addressLabelNL(meterDetail, kind) {
  if (!meterDetail) return ''
  const houseNumber = kind === 'cvz'
    ? String(meterDetail.house_number ?? '').replace(/[\s_-]*CVZ.*$/i, '')
    : meterDetail.house_number
  return `${meterDetail.street ?? ''} ${houseNumber ?? ''}`.trim()
}

/**
 * How long ago, in Dutch.
 * @param {string|Date} d - Date string or Date object
 * @returns {string} e.g. "3 uur geleden", "7 jaar geleden"
 */
export function formatRelativeNL(d) {
  if (!d) return '\u2014'
  return formatDistanceToNowStrict(new Date(d), { locale: nl, addSuffix: true })
}

/**
 * Format a date string to Dutch day + date format.
 * @param {string} dateStr - Date string in yyyy-MM-dd format
 * @returns {string} e.g. "ma 3 mrt"
 */
export function formatDayNL(dateStr) {
  const [, m, d] = dateStr.split('-').map(Number)
  const dayOfWeek = DAY_NAMES_NL[new Date(dateStr).getDay()]
  return `${dayOfWeek} ${d} ${MONTH_NAMES_NL[m - 1]}`
}

/**
 * Format a date string to short day format.
 * @param {string} dateStr - Date string in yyyy-MM-dd format
 * @returns {string} e.g. "3-3"
 */
export function formatDateShort(dateStr) {
  const [, m, d] = dateStr.split('-').map(Number)
  return `${d}-${m}`
}

/**
 * Get Dutch month name by index.
 * @param {number} monthIndex - 0-based month index
 * @returns {string} e.g. "mrt"
 */
export function monthNameNL(monthIndex) {
  return MONTH_NAMES_NL[monthIndex] || ''
}

/**
 * Format a number in Dutch notation, '—' when missing.
 * @param {number|string|null} n
 * @param {number} maximumFractionDigits
 * @returns {string} e.g. "1.234,5"
 */
export function formatDecimal(n, maximumFractionDigits = 1) {
  return n == null || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('nl-NL', { maximumFractionDigits })
}

// Export arrays for direct use in analysis engines
export { MONTH_NAMES_NL, DAY_NAMES_NL }
